import { applyMiddleware, compose, createStore as reduxCreateStore } from 'redux';
import { createReduxHistoryContext } from 'redux-first-history';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createFilter } from 'redux-persist-transform-filter';
import thunkMiddleware from 'redux-thunk';

import { createBrowserHistory, createMemoryHistory } from 'history';

import getEnv from 'utils/getEnv';

import { signOutUserWithExpiredTokensOnLocationChange } from 'dux/auth/middlewares';
import cartInitialState from 'dux/checkoutCart/initialState';
import { fetchFeatureFlagsOnAuthSuccess } from 'dux/featureFlags/middlewares';
import { fetchGiftWithPurchaseOnAuthSuccess } from 'dux/giftWithPurchase/middlewares';
import ownRouterMiddleware from 'dux/router/middleware';
import trackers from 'dux/tracking/middleware';

import errorMiddleware from './Middleware/errorMiddleware';
import sentryMiddleware from './Middleware/sentryMiddleware';
import createRootReducer from './Reducers';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: typeof window !== 'undefined' ? createBrowserHistory() : createMemoryHistory(),
  // reduxTravelling: true,
  // showHistoryAction: true,
});

export const rootReducer = createRootReducer(routerReducer);

const middlewares = [
  /* our custom route middleware */
  ownRouterMiddleware,
  /* the one from redux-history-first */
  routerMiddleware,
  thunkMiddleware,
  sentryMiddleware,
  trackers,
  fetchFeatureFlagsOnAuthSuccess,
  fetchGiftWithPurchaseOnAuthSuccess,
  signOutUserWithExpiredTokensOnLocationChange,
];

if (
  getEnv('NODE_ENV') === 'development' &&
  getEnv('REACT_APP_REDUX_LOGGER') !== 'False' &&
  typeof window !== 'undefined' // Not on Next.js server for lisibility
) {
  // requiring inside the if statement avoid getting it in the production bundle
  const { logger } = require('redux-logger');
  middlewares.push(logger);
}

middlewares.push(errorMiddleware);

const getPreloadedState = () => ({
  checkout: {
    cart: {
      ...cartInitialState,
    },
  },
});

const consultationSubsetFilter = createFilter('consultation', ['bufferedAnswers']);

const persistConfig = {
  key: 'consultation',
  storage,
  whitelist: ['consultation'], // only consultation will be persisted
  transforms: [consultationSubsetFilter],
};
const persistedReducer = persistReducer(persistConfig, rootReducer);

const composeEnhancers =
  typeof window !== 'undefined' &&
  typeof window === 'object' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose;

const createStore = () => {
  const store = reduxCreateStore(
    typeof window !== 'undefined' ? persistedReducer : rootReducer,
    getPreloadedState(),
    composeEnhancers(applyMiddleware(...middlewares))
  );
  // Recommended way of accessing redux store from cypress https://www.cypress.io/blog/2018/11/14/testing-redux-store/
  if (typeof window !== 'undefined' && window.Cypress) {
    window.store = store;
  }
  return store;
};

export const createTestStore = () =>
  reduxCreateStore(
    rootReducer,
    composeEnhancers(applyMiddleware(...[routerMiddleware, thunkMiddleware]))
  );

const store = createStore();
export default store;

export const history = createReduxHistory(store);
